import { usePortalPartnerBrand } from '@/hooks/portal-partner-brand';
import { FAQ_ROUTE } from '@/routes/route-paths';
import { Alert, AlertTitle, Box, Button, Stack, Typography } from '@montugroup/design-system';
import { BrandThemeProvider } from '@montugroup/themes';
import { styled } from '@mui/material';
import { Link, Link as RouterLink } from 'react-router-dom';
import { PaymentOptions } from './PaymentOptions';

type OrderMedicationCardProps = {
  primaryButtonText?: string;
  primaryButtonLink: string;
  secondaryButtonText?: string;
  secondaryButtonLink?: string;
  showPrimaryButton?: boolean;
  showSecondaryButton?: boolean;
  showDischargedBanner?: boolean;
};

const OrderCard = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(0, 0, 0, 0.05)',
  boxShadow: theme.shadows[1],
  justifyContent: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(6),

  [theme.breakpoints.up('md')]: {
    height: '100%',
  },
}));

const TextWithLogo = styled(Box)(({ theme }) => ({
  position: 'relative',
  bottom: theme.spacing(2),
  '& img': {
    width: '5.3125rem',
    height: '1.375rem',
    position: 'absolute',
    left: theme.spacing(18),
    bottom: theme.spacing(-0.5),
  },
}));

export function OrderMedicationCard({
  primaryButtonText = 'Start your order',
  primaryButtonLink,
  secondaryButtonText,
  secondaryButtonLink,
  showPrimaryButton = true,
  showSecondaryButton = true,
  showDischargedBanner = false,
}: OrderMedicationCardProps) {
  const { key: partnerKey, Logo: PartnerLogo } = usePortalPartnerBrand();

  return (
    <BrandThemeProvider theme={partnerKey}>
      <OrderCard>
        <TextWithLogo>
          <Typography variant="subtitle2" sx={(theme) => ({ color: theme.palette.primary.main })} fontWeight="bold">
            Order my medication
          </Typography>

          <Typography
            variant="body2"
            sx={(theme) => ({ color: theme.palette.primary.main, marginTop: theme.spacing(1) })}
          >
            Powered by <PartnerLogo fillColor="currentColor" />
          </Typography>
        </TextWithLogo>
        {showDischargedBanner && (
          <Alert
            severity="info"
            sx={{
              width: '100%',
              mb: 2,
            }}
          >
            <AlertTitle>You have been discharged from uMeds.</AlertTitle>
            To resume access to uMeds, please{' '}
            <Link to={FAQ_ROUTE} style={{ textDecoration: 'underline', color: 'inherit' }}>
              contact us
            </Link>
            .
          </Alert>
        )}
        {showPrimaryButton && (
          <Button
            component={RouterLink}
            to={primaryButtonLink}
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            data-testid="umeds-home-order-btn"
            disabled={showDischargedBanner}
          >
            {primaryButtonText}
          </Button>
        )}

        {showSecondaryButton && secondaryButtonText && secondaryButtonLink && (
          <Button variant="outlined" color="primary" component={RouterLink} to={secondaryButtonLink} size="large">
            {secondaryButtonText}
          </Button>
        )}
        <PaymentOptions />
      </OrderCard>
    </BrandThemeProvider>
  );
}

export default OrderMedicationCard;
